import React, { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  editConnectorRating,
  fetchAllConnectorRatings,
} from '../../../redux/slices/ConnectorRatingSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';

interface EditRatingProperties {
  ratingDetails: any;
  closeModal: () => void;
}

/**
 * Handle edit Rating
 * @param {any} param0 - Current rating data
 * @param {any} param0.ratingDetails - The ratingDetails prop
 * @param {any} param0.closeModal - The closeModal prop
 * @returns {any} JSX.Element
 */
function EditRating({ ratingDetails, closeModal }: EditRatingProperties) {
  const dispatch = useAppDispatch();
  const [editRatingDetails, setEditRatingDetails] = useState({
    ratingName: ratingDetails?.connectorRatingName || '',
    currentType: ratingDetails?.type || 'AC',
    maxPowerOutput: ratingDetails?.maxPowerOutput || '',
    active: ratingDetails?.status || false,
  });
  const [disabled, setDisabled] = useState(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [modalView, setModalView] = useState({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const { page, limit } = useAppSelector((state) => state.connectorRating);

  /**
   * Handle the input change function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | boolean) => {
    setEditRatingDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle submit function
   */
  const handleSubmit = async () => {
    setDisabled(true);

    try {
      await dispatch(
        editConnectorRating({
          id: ratingDetails.id,
          connectorRatingName: editRatingDetails.ratingName,
          type: editRatingDetails.currentType,
          maxPowerOutput: editRatingDetails.maxPowerOutput,
          status: editRatingDetails.active,
        }),
      ).unwrap();
      dispatch(
        fetchAllConnectorRatings({
          page,
          limit,
        }),
      );
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title="Connector Rating Has Been  Updated Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              closeModal();
            }}
          />
        ),
      });
    } catch (error) {
      console.log(error);
    } finally {
      setDisabled(false);
    }
  };

  return (
    <>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        header={modalView.header}
        content={modalView.content}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
        closeModal={() => setOpenStatus(false)}
      />
      <div className="add-rating-side-panel">
        <div className="panel-header">
          <p>Edit Connector Ratings</p>
        </div>

        <div style={{ height: window.innerHeight >= 700 ? 398 : 710 }}>
          <div className="add-rating-group">
            <div className="profile-card">
              <div className="edit-fields-container">
                <div className="info-row-input-single">
                  <CustomInput
                    name="ratingName"
                    inputLabel="Rating Name"
                    type="text"
                    value={editRatingDetails.ratingName}
                    placeholder="Enter Rating Name"
                    onChangeValue={handleInputChange}
                  />
                </div>

                <div className="info-row-input">
                  <SelectField
                    name="currentType"
                    inputLabel="Current Type"
                    selectedValue={editRatingDetails.currentType}
                    placeholder="Select Type"
                    onChangeValue={handleInputChange}
                    options={[
                      {
                        label: 'AC',
                        value: 'AC',
                      },
                      {
                        label: 'DC',
                        value: 'DC',
                      },
                      {
                        label: 'Hybrid',
                        value: 'Hybrid',
                      },
                    ]}
                  />

                  <CustomInput
                    name="maxPowerOutput"
                    inputLabel="Max Power Output"
                    type="text"
                    value={editRatingDetails.maxPowerOutput}
                    placeholder="Enter Power"
                    onChangeValue={handleInputChange}
                    svgIcon={
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#666',
                          paddingRight: '4px',
                        }}>
                        kW
                      </span>
                    }
                  />
                </div>

                <div className="checkbox-section">
                  <CheckBoxButtons
                    name="active"
                    list={[
                      {
                        label: 'Marks As Active',
                        value: 'active',
                      },
                    ]}
                    value={editRatingDetails.active ? ['active'] : []}
                    onChangeValue={(name: string, value: any) => {
                      handleInputChange(name, value.includes('active'));
                    }}
                  />

                  <p className="checkbox-description">
                    Inactive Ratings Will Not Be Available For Selection
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-actions">
            <MyButton
              label="Cancel"
              buttonType="outline"
              onClickFunc={() => {
                closeModal();
              }}
            />

            <MyButton
              disableButton={disabled}
              loading={disabled}
              label="Update"
              buttonType="submit"
              onClickFunc={handleSubmit}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default EditRating;
